export function ParticipantRowSkeleton() {
  return (
    <li className="flex items-center justify-between px-3 py-2.5 gap-2">
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="h-3 w-24 bg-gray-200 rounded-full" />
          <div className="h-2.5 w-14 bg-gray-100 rounded-full" />
        </div>
      </div>
      <div className="flex items-center gap-1 ml-1 shrink-0">
        <div className="h-7 w-7 rounded-md bg-gray-100" />
        <div className="h-8 w-[64px] rounded-lg bg-gray-100" />
      </div>
    </li>
  );
}

export function ParticipantListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <ul className="divide-y animate-pulse">
      {Array.from({ length: count }, (_, i) => (
        <ParticipantRowSkeleton key={i} />
      ))}
    </ul>
  );
}

export function SpaceCardListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }, (_, i) => (
        <SpaceCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function TeacherSpaceListSkeleton() {
  return (
    <div className="flex flex-col gap-8 animate-pulse">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div className="h-7 w-36 bg-gray-200 rounded-full" />
          <div className="flex items-center gap-2">
            <div className="h-9 w-24 bg-gray-100 rounded-xl" />
            <div className="h-9 w-9 bg-gray-100 rounded-xl" />
            <div className="h-9 w-44 bg-gray-100 rounded-xl" />
          </div>
        </div>
        <SpaceCardSkeleton />
        <SpaceCardSkeleton />
        <SpaceCardSkeleton />
      </div>
    </div>
  );
}

export function SpaceCardSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-200 px-6 py-5 animate-pulse">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          <div className="h-5 w-14 bg-gray-200 rounded-full" />
          <div className="h-7 w-48 bg-gray-200 rounded-full" />
          <div className="h-4 w-64 bg-gray-100 rounded-full" />
        </div>
        <div className="flex gap-2 shrink-0">
          <div className="h-9 w-20 bg-gray-100 rounded-xl" />
          <div className="h-9 w-28 bg-gray-100 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

export function EditSpaceModalSkeleton() {
  return (
    <div className="flex flex-col gap-5 px-6 py-5 animate-pulse">
      {/* Space name */}
      <div className="flex flex-col gap-1.5">
        <div className="h-3.5 w-20 bg-gray-200 rounded-full" />
        <div className="h-11 rounded-xl bg-gray-100" />
      </div>
      {/* Classrooms */}
      <div className="flex flex-col gap-1.5">
        <div className="h-3.5 w-24 bg-gray-200 rounded-full" />
        <div className="h-12 rounded-xl bg-gray-100" />
        <div className="h-3 w-64 bg-gray-100 rounded-full" />
      </div>
      {/* Lessons (optional) */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-16 bg-gray-200 rounded-full" />
          <div className="h-3.5 w-14 bg-gray-100 rounded-full" />
        </div>
        <div className="h-12 rounded-xl bg-gray-100" />
        <div className="h-3 w-52 bg-gray-100 rounded-full" />
      </div>
      {/* Description (optional) */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-24 bg-gray-200 rounded-full" />
          <div className="h-3.5 w-14 bg-gray-100 rounded-full" />
        </div>
        <div className="h-11 rounded-xl bg-gray-100" />
        <div className="h-3 w-56 bg-gray-100 rounded-full" />
      </div>
      {/* Instructions to AI (optional) — textarea rows=3 */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <div className="h-3.5 w-32 bg-gray-200 rounded-full" />
          <div className="h-3.5 w-14 bg-gray-100 rounded-full" />
        </div>
        <div className="h-[88px] rounded-xl bg-gray-100" />
        <div className="h-3 w-60 bg-gray-100 rounded-full" />
      </div>
      {/* Footer: divider + buttons */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
        <div className="h-3.5 w-12 bg-gray-100 rounded-full" />
        <div className="h-9 w-28 rounded-xl bg-gray-200" />
      </div>
    </div>
  );
}

export function RecapPageSkeleton() {
  return (
    <div className="flex flex-col gap-8 animate-pulse">
      {/* Header: text-4xl title + text-sm subtitle + h-9 button */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="h-10 w-72 bg-gray-200 rounded-full" />
          <div className="h-4 w-48 bg-gray-100 rounded-full mt-1" />
        </div>
        <div className="h-9 w-32 bg-gray-100 rounded-xl shrink-0" />
      </div>
      {/* KPI cards: text-xs label + text-3xl value */}
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4 flex flex-col gap-1"
          >
            <div className="h-3.5 w-24 bg-gray-200 rounded-full" />
            <div className="h-9 w-20 bg-gray-200 rounded-full mt-0.5" />
          </div>
        ))}
      </div>
      {/* Students list: text-xl header + divider + avatar + two lines */}
      <div className="rounded-2xl border border-gray-200 bg-white px-6 py-5 flex flex-col">
        <div className="h-7 w-24 bg-gray-200 rounded-full mb-4" />
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <div className="h-px bg-gray-100 mx-1" />
            <div className="flex items-center gap-3 py-3">
              <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0" />
              <div className="flex flex-col gap-1.5 flex-1">
                <div className="h-4 w-36 bg-gray-200 rounded-full" />
                <div className="h-3.5 w-24 bg-gray-100 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
